import { useEffect, useState } from 'react';

const scrollTo = (id: string) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="home" className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image — premium legal environment */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9b2dac0ef7d31a0cfd18c4d27b6a0a72.jpg"
          alt="Gamble & Garcia Attorneys at Law — Santa Maria California"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer dark overlay for maximum text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-black/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-black/40"></div>
      </div>

      {/* Gold left accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-[#C9A84C]/50 to-transparent hidden lg:block"></div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-28 flex flex-col items-center text-center">

        {/* Location eyebrow */}
        <div
          className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
        >
          <div className="inline-flex items-center gap-4 mb-10">
            <div className="w-10 h-[1px] bg-[#C9A84C]/50"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.5em] uppercase font-light">
              Santa Maria &bull; San Luis Obispo &bull; Central Coast, California
            </span>
            <div className="w-10 h-[1px] bg-[#C9A84C]/50"></div>
          </div>
        </div>

        {/* H1 — outcome-driven, location-anchored */}
        <h1
          className={`font-serif text-white text-5xl md:text-6xl lg:text-[76px] xl:text-[84px] font-bold leading-[1.06] mb-8 max-w-5xl transition-all ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{ transitionDuration: '900ms', transitionDelay: '200ms' }}
        >
          Strategic Legal Representation<br />
          <em className="not-italic text-[#C9A84C]">Built to Win</em>
        </h1>

        {/* Subheadline */}
        <p
          className={`text-white/70 text-lg md:text-xl max-w-2xl leading-[1.8] mb-14 transition-all ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDuration: '800ms', transitionDelay: '350ms' }}
        >
          Experienced California attorneys delivering aggressive, results-focused legal strategies — protecting your position and securing the best possible outcome in criminal defense, personal injury, family law, and civil litigation.
        </p>

        {/* CTAs */}
        <div
          className={`flex flex-col sm:flex-row gap-4 mb-16 transition-all ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDuration: '800ms', transitionDelay: '500ms' }}
        >
          <button
            onClick={() => scrollTo('#contact')}
            className="px-10 py-4.5 bg-[#C9A84C] text-black font-bold text-[12px] tracking-[0.28em] uppercase rounded-sm hover:bg-[#b8943e] active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ paddingTop: '18px', paddingBottom: '18px' }}
          >
            Speak With an Attorney Today
          </button>
          <a
            href="tel:8059228308"
            className="px-10 py-4.5 border-2 border-white/60 text-white font-bold text-[12px] tracking-[0.28em] uppercase rounded-sm hover:bg-white hover:text-black active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2.5"
            style={{ paddingTop: '18px', paddingBottom: '18px' }}
          >
            <i className="ri-phone-fill text-sm"></i>
            (805) 922-8308
          </a>
        </div>

        {/* Trust signals row */}
        <div
          className={`flex flex-wrap items-center justify-center gap-x-10 gap-y-4 transition-all ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDuration: '700ms', transitionDelay: '650ms' }}
        >
          {[
            { icon: 'ri-star-fill', text: '4.8 Google Rating' },
            { icon: 'ri-shield-check-fill', text: 'Martindale-Hubbell Distinguished' },
            { icon: 'ri-scales-3-fill', text: '50+ Years Combined Experience' },
            { icon: 'ri-map-pin-2-fill', text: '2 Office Locations' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-[#C9A84C] text-sm`}></i>
              </div>
              <span className="text-white/55 text-[12px] tracking-wider">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <div
          className={`mt-24 flex flex-col items-center gap-2 transition-all ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDuration: '700ms', transitionDelay: '900ms' }}
        >
          <span className="text-white/25 text-[9px] tracking-[0.5em] uppercase">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-[#C9A84C]/50 to-transparent animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
